import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import { Home, BookOpen, ClipboardList, Award, UserRound, LifeBuoy, LayoutDashboard, Users, Link2 } from "lucide-react";
import { Toaster } from "sonner";
import { AuthProvider, RequireRole, useAuth, homeFor } from "./context/AuthContext";
import { FullLoader } from "./components/common";
import Shell from "./components/Shell";
import Login from "./pages/Login";
import ForgotPassword, { ResetPassword } from "./pages/ForgotPassword";
import Verify from "./pages/Verify";
import Dashboard from "./pages/student/Dashboard";
import Courses from "./pages/student/Courses";
import CourseDetail from "./pages/student/CourseDetail";
import Lesson from "./pages/student/Lesson";
import Exams from "./pages/student/Exams";
import ExamTake from "./pages/student/ExamTake";
import Certificates from "./pages/student/Certificates";
import Profile from "./pages/student/Profile";
import Support from "./pages/student/Support";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminStudents from "./pages/admin/AdminStudents";
import AdminCourses from "./pages/admin/AdminCourses";
import AdminAssignments from "./pages/admin/AdminAssignments";
import AdminExams from "./pages/admin/AdminExams";
import AdminCertificates from "./pages/admin/AdminCertificates";

const STUDENT_NAV = [
  { to: "/panel", label: "Ana Sayfa", icon: Home, end: true, testid: "nav-home" },
  { to: "/panel/egitimlerim", label: "Eğitimlerim", icon: BookOpen, testid: "nav-courses" },
  { to: "/panel/sinavlarim", label: "Sınavlarım", icon: ClipboardList, testid: "nav-exams" },
  { to: "/panel/sertifikalarim", label: "Sertifikalarım", icon: Award, testid: "nav-certificates" },
  { to: "/panel/profil", label: "Profilim", icon: UserRound, testid: "nav-profile" },
  { to: "/panel/destek", label: "Destek", icon: LifeBuoy, testid: "nav-support" },
];

const ADMIN_NAV = [
  { to: "/admin", label: "Genel Bakış", icon: LayoutDashboard, end: true, testid: "nav-admin-home" },
  { to: "/admin/kursiyerler", label: "Kursiyerler", icon: Users, testid: "nav-admin-students" },
  { to: "/admin/egitimler", label: "Eğitimler", icon: BookOpen, testid: "nav-admin-courses" },
  { to: "/admin/atamalar", label: "Eğitim Atama", icon: Link2, testid: "nav-admin-assignments" },
  { to: "/admin/sinavlar", label: "Sınavlar", icon: ClipboardList, testid: "nav-admin-exams" },
  { to: "/admin/sertifikalar", label: "Sertifikalar", icon: Award, testid: "nav-admin-certificates" },
  { to: "/admin/profil", label: "Profilim", icon: UserRound, testid: "nav-admin-profile" },
];

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <FullLoader />;
  return user ? <Navigate to={homeFor(user)} replace /> : children;
}

function RootRedirect() {
  const { user } = useAuth();
  if (user === null) return <FullLoader />;
  return <Navigate to={user ? homeFor(user) : "/giris"} replace />;
}

export default function App() {
  return (
    <MotionConfig reducedMotion="user">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/giris" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/sifremi-unuttum" element={<ForgotPassword />} />
            <Route path="/sifre-sifirla" element={<ResetPassword />} />
            <Route path="/dogrula" element={<Verify />} />
            <Route path="/dogrula/:number" element={<Verify />} />
            <Route path="/panel" element={<RequireRole role="student"><Shell items={STUDENT_NAV} badge="Kursiyer Paneli" /></RequireRole>}>
              <Route index element={<Dashboard />} />
              <Route path="egitimlerim" element={<Courses />} />
              <Route path="egitimlerim/:courseId" element={<CourseDetail />} />
              <Route path="egitimlerim/:courseId/ders/:lessonId" element={<Lesson />} />
              <Route path="sinavlarim" element={<Exams />} />
              <Route path="sinavlarim/:examId" element={<ExamTake />} />
              <Route path="sertifikalarim" element={<Certificates />} />
              <Route path="profil" element={<Profile />} />
              <Route path="destek" element={<Support />} />
            </Route>
            <Route path="/admin" element={<RequireRole role="admin"><Shell items={ADMIN_NAV} badge="Yönetici Paneli" /></RequireRole>}>
              <Route index element={<AdminDashboard />} />
              <Route path="kursiyerler" element={<AdminStudents />} />
              <Route path="egitimler" element={<AdminCourses />} />
              <Route path="atamalar" element={<AdminAssignments />} />
              <Route path="sinavlar" element={<AdminExams />} />
              <Route path="sertifikalar" element={<AdminCertificates />} />
              <Route path="profil" element={<Profile />} />
            </Route>
            <Route path="*" element={<RootRedirect />} />
          </Routes>
          <Toaster position="top-right" richColors closeButton toastOptions={{ style: { borderRadius: 14, fontFamily: "Plus Jakarta Sans, sans-serif" } }} />
        </AuthProvider>
      </BrowserRouter>
    </MotionConfig>
  );
}
